import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";

const CrowdCanvas = ({
  src,

  // Sprite sheet configuration
  rows = 15,
  cols = 7,

  // Crowd configuration
  peepCount = null,
  speed = 1,
  minSpeed = 0.5,
  maxSpeed = 1.5,

  // Movement
  direction = "random", // "random" | "left" | "right"
  bounce = true,
  bounceHeight = 10,

  // Vertical positioning
  verticalSpread = 250,
  verticalOffset = 100,

  // Canvas
  height = "100%",
  width = "100%",
  className = "",

  // Rendering
  zIndex = 0,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || !src) return;

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    const stage = {
      width: 0,
      height: 0,
    };

    const allPeeps = [];
    const availablePeeps = [];
    const crowd = [];

    /*
     * -------------------------------------------------------
     * UTILS
     * -------------------------------------------------------
     */

    const randomRange = (min, max) => {
      return min + Math.random() * (max - min);
    };

    const randomIndex = (array) => {
      return Math.floor(Math.random() * array.length);
    };

    const removeFromArray = (array, index) => {
      return array.splice(index, 1)[0];
    };

    const removeRandomFromArray = (array) => {
      return removeFromArray(array, randomIndex(array));
    };

    // const getRandomFromArray = (array) => {
    //   return array[randomIndex(array)];
    // };

    /*
     * -------------------------------------------------------
     * RESET PEEP
     * -------------------------------------------------------
     */

    const resetPeep = (peep) => {
      let peepDirection;

      if (direction === "random") {
        peepDirection = Math.random() > 0.5 ? 1 : -1;
      } else if (direction === "left") {
        peepDirection = -1;
      } else {
        peepDirection = 1;
      }

      const offsetY =
        verticalOffset -
        verticalSpread * gsap.parseEase("power2.in")(Math.random());

      const startY =
        stage.height -
        peep.height +
        offsetY;

      let startX;
      let endX;

      if (peepDirection === 1) {
        // Left → Right
        startX = -peep.width;
        endX = stage.width;

        peep.scaleX = 1;
      } else {
        // Right → Left
        startX = stage.width + peep.width;
        endX = 0;

        peep.scaleX = -1;
      }

      peep.x = startX;
      peep.y = startY;
      peep.anchorY = startY;

      return {
        startX,
        startY,
        endX,
      };
    };

    /*
     * -------------------------------------------------------
     * WALK ANIMATION
     * -------------------------------------------------------
     */

    const normalWalk = ({ peep, props }) => {
      const { startY, endX } = props;

      /*
       * Higher speed value = faster movement
       */
      const baseDuration = 10;

      const xDuration = baseDuration / speed;

      const randomTimeScale = randomRange(
        minSpeed,
        maxSpeed
      );

      const yDuration = bounce ? 0.25 : 0;

      const timeline = gsap.timeline();

      timeline.timeScale(randomTimeScale);

      /*
       * Horizontal movement
       */
      timeline.to(
        peep,
        {
          duration: xDuration,
          x: endX,
          ease: "none",
        },
        0
      );

      /*
       * Small vertical bounce
       */
      if (bounce) {
        timeline.to(
          peep,
          {
            duration: yDuration,
            repeat: Math.max(
              1,
              Math.floor(xDuration / yDuration)
            ),
            yoyo: true,
            y: startY - bounceHeight,
            ease: "sine.inOut",
          },
          0
        );
      }

      return timeline;
    };

    /*
     * -------------------------------------------------------
     * CREATE PEEP
     * -------------------------------------------------------
     */

    const createPeep = ({
      image,
      rect,
    }) => {
      const peep = {
        image,

        rect: [],

        width: 0,
        height: 0,

        x: 0,
        y: 0,

        anchorY: 0,

        scaleX: 1,

        walk: null,

        setRect(newRect) {
          peep.rect = newRect;

          peep.width = newRect[2];
          peep.height = newRect[3];
        },

        render(context) {
          context.save();

          context.translate(
            peep.x,
            peep.y
          );

          context.scale(
            peep.scaleX,
            1
          );

          context.drawImage(
            peep.image,

            peep.rect[0],
            peep.rect[1],
            peep.rect[2],
            peep.rect[3],

            0,
            0,
            peep.width,
            peep.height
          );

          context.restore();
        },
      };

      peep.setRect(rect);

      return peep;
    };

    /*
     * -------------------------------------------------------
     * CREATE ALL SPRITE PEEPS
     * -------------------------------------------------------
     */

    const createPeeps = (image) => {
      const totalSprites = rows * cols;

      const rectWidth =
        image.naturalWidth / rows;

      const rectHeight =
        image.naturalHeight / cols;

      for (let i = 0; i < totalSprites; i++) {
        const rect = [
          (i % rows) * rectWidth,

          Math.floor(i / rows) * rectHeight,

          rectWidth,

          rectHeight,
        ];

        allPeeps.push(
          createPeep({
            image,
            rect,
          })
        );
      }
    };

    /*
     * -------------------------------------------------------
     * CROWD MANAGEMENT
     * -------------------------------------------------------
     */

    const removePeepFromCrowd = (peep) => {
      const index = crowd.indexOf(peep);

      if (index !== -1) {
        crowd.splice(index, 1);
      }

      availablePeeps.push(peep);
    };

    const addPeepToCrowd = () => {
      if (
        availablePeeps.length === 0
      ) {
        return null;
      }

      const peep =
        removeRandomFromArray(
          availablePeeps
        );

      const walk = normalWalk({
        peep,

        props: resetPeep(peep),
      });

      walk.eventCallback(
        "onComplete",
        () => {
          removePeepFromCrowd(peep);

          addPeepToCrowd();
        }
      );

      peep.walk = walk;

      crowd.push(peep);

      /*
       * Draw people from back → front
       */
      crowd.sort(
        (a, b) =>
          a.anchorY - b.anchorY
      );

      return peep;
    };

    const initCrowd = () => {
      /*
       * If peepCount is not provided,
       * use every available sprite.
       */
      const count =
        peepCount === null
          ? allPeeps.length
          : Math.min(
              peepCount,
              allPeeps.length
            );

      for (let i = 0; i < count; i++) {
        const peep =
          addPeepToCrowd();

        if (peep?.walk) {
          /*
           * Start each person at a
           * different point in their walk.
           */
          peep.walk.progress(
            Math.random()
          );
        }
      }
    };

    /*
     * -------------------------------------------------------
     * RENDER
     * -------------------------------------------------------
     */

    const render = () => {
      ctx.clearRect(
        0,
        0,
        canvas.width,
        canvas.height
      );

      ctx.save();

      ctx.scale(
        dpr,
        dpr
      );

      crowd.forEach(
        (peep) => {
          peep.render(ctx);
        }
      );

      ctx.restore();
    };

    /*
     * -------------------------------------------------------
     * RESIZE
     * -------------------------------------------------------
     */

    const resize = () => {
      stage.width =
        canvas.clientWidth;

      stage.height =
        canvas.clientHeight;

      canvas.width =
        stage.width * dpr;

      canvas.height =
        stage.height * dpr;

      /*
       * Kill existing animations
       */
      crowd.forEach(
        (peep) => {
          if (peep.walk) {
            peep.walk.kill();
          }
        }
      );

      crowd.length = 0;

      availablePeeps.length = 0;

      availablePeeps.push(
        ...allPeeps
      );

      initCrowd();
    };

    /*
     * -------------------------------------------------------
     * IMAGE
     * -------------------------------------------------------
     */

    const image =
      new Image();

    image.onload = () => {
      /*
       * Prevent duplicate initialization
       */
      if (allPeeps.length > 0) {
        return;
      }

      createPeeps(image);

      resize();

      gsap.ticker.add(render);
    };

    image.src = src;

    /*
     * -------------------------------------------------------
     * RESIZE LISTENER
     * -------------------------------------------------------
     */

    window.addEventListener(
      "resize",
      resize
    );

    /*
     * -------------------------------------------------------
     * CLEANUP
     * -------------------------------------------------------
     */

    return () => {
      window.removeEventListener(
        "resize",
        resize
      );

      gsap.ticker.remove(
        render
      );

      crowd.forEach(
        (peep) => {
          if (peep.walk) {
            peep.walk.kill();
          }
        }
      );
    };
  }, [
    src,
    rows,
    cols,
    peepCount,
    speed,
    minSpeed,
    maxSpeed,
    direction,
    bounce,
    bounceHeight,
    verticalSpread,
    verticalOffset,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute bottom-0 left-0 block ${className}`}
      style={{
        width,
        height,
        zIndex,
      }}
    />
  );
};

export default CrowdCanvas;